import { formatXml, parseXml, type XmlNode } from '@/lib/xml';

function cloneNode(node: XmlNode): XmlNode {
  return {
    ...node,
    attributes: { ...node.attributes },
    children: node.children.map(cloneNode),
  };
}

/** Walk Document-relative path (no leading Document/). */
function findNode(root: XmlNode, path: string): XmlNode | null {
  const parts = path.split('/').filter(Boolean);
  const cur: XmlNode | null = root.name === 'Document' ? root : null;
  if (!cur) return null;
  if (parts.length === 0) return cur;

  let hit: XmlNode | null = null;
  for (const part of parts) {
    const nodes: XmlNode[] = hit !== null ? hit.children : cur.children;
    hit = nodes.find((n: XmlNode) => n.name === part) ?? null;
    if (!hit) return null;
  }
  return hit;
}

function findParent(root: XmlNode, path: string): { parent: XmlNode; name: string } | null {
  const parts = path.split('/').filter(Boolean);
  if (parts.length === 0) return null;
  const name = parts[parts.length - 1]!;
  const parentPath = parts.slice(0, -1).join('/');
  const parent = parentPath ? findNode(root, parentPath) : root.name === 'Document' ? root : null;
  if (!parent) return null;
  return { parent, name };
}

function escapeText(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function escapeAttr(s: string): string {
  return escapeText(s).replace(/"/g, '&quot;');
}

function serialize(node: XmlNode): string {
  const attrs = Object.entries(node.attributes)
    .map(([k, v]) => ` ${k}="${escapeAttr(v)}"`)
    .join('');
  if (node.children.length === 0) {
    if (node.text == null || node.text === '') return `<${node.name}${attrs}/>`;
    return `<${node.name}${attrs}>${escapeText(node.text)}</${node.name}>`;
  }
  const inner = node.children.map(serialize).join('');
  return `<${node.name}${attrs}>${inner}</${node.name}>`;
}

function withRoot(xml: string, mutate: (root: XmlNode) => void): string | null {
  const parsed = parseXml(xml);
  if (!parsed.wellFormed || !parsed.root) return null;
  const root = cloneNode(parsed.root);
  mutate(root);
  const decl = xml.trimStart().startsWith('<?xml') ? xml.trimStart().match(/^<\?xml[^?]*\?>\s*/)?.[0] ?? '' : '';
  return formatXml(`${decl}${serialize(root)}`);
}

/** Set leaf text at Document-relative path. */
export function setXmlText(xml: string, path: string, value: string): string | null {
  return withRoot(xml, (root) => {
    const node = findNode(root, path);
    if (!node || node.children.length > 0) return;
    node.text = value;
  });
}

/** Insert an empty child element under parentPath (Document-relative). */
export function insertXmlChild(xml: string, parentPath: string, childName: string): string | null {
  return withRoot(xml, (root) => {
    const parent = parentPath ? findNode(root, parentPath) : root;
    if (!parent) return;
    const depth = parent.depth + 1;
    const path = parentPath ? `${parentPath}/${childName}` : childName;
    // Insert under Document's message root path: parent.path may include Document/
    parent.children.push({
      name: childName,
      path: parent.name === 'Document' ? childName : path,
      attributes: {},
      text: '',
      children: [],
      depth,
    });
  });
}

/** Remove the first element at Document-relative path. */
export function removeXmlNode(xml: string, path: string): string | null {
  return withRoot(xml, (root) => {
    const hit = findParent(root, path);
    if (!hit) return;
    const idx = hit.parent.children.findIndex((c) => c.name === hit.name);
    if (idx >= 0) hit.parent.children.splice(idx, 1);
  });
}

/** Children names present under a Document-relative parent path. */
export function childNamesAt(xml: string, parentPath: string): string[] {
  const parsed = parseXml(xml);
  if (!parsed.root) return [];
  const parent = parentPath ? findNode(parsed.root, parentPath) : parsed.root;
  if (!parent) return [];
  return parent.children.map((c) => c.name);
}

export function textAt(xml: string, path: string): string | null {
  const parsed = parseXml(xml);
  if (!parsed.root) return null;
  const node = findNode(parsed.root, path);
  if (!node || node.children.length > 0) return null;
  return node.text ?? '';
}
